import { utils } from '@hh/global.js'

/**
 * ex usage
 * assign id = your id
 * <hh-swiper data-id={your id}>
 *  <div class='swiper swiper--{{your id}}`> // .swiper for styles, .swiper--{{id}} for js
 *    <div class='swiper-wrapper'>
 *      <div class='swiper-slide' </div>
 *    </div>
 *    <div class='swiper-button-prev swiper-button-prev--{{your id}}></div>
 *   <div class='swiper-button-next swiper-button-next--{{your id}}></div>
 *  </div>
 * </hh-swiper>
 *
 *
 * use data-mobile-nav="true" to enable navigation on mobile (ie, the product card images and announcement bar don't show arrows on mobile)
 * data-slides-per-view should be a comma separated string of three values for mobile, tablet, and desktop. ex: "1,2,3"
 * data-space-between should be a comma separated string of three values for mobile, tablet, and desktop. ex: "1,2,3"
 * data-custom-next and data-custom-previous can be used to specify custom classes for the next and previous buttons. See the `product-feed` section for how it's done
 * data-effect can be fade, cube, flip.. all options from swiper.js. Default is slide
 * data-pdp-media-gallery="true" enables the PDP zoom button for the active slide
 */

const openPdpMediaModal = (modalId, mediaId) => {
  document.body.setAttribute(
    'modal',
    JSON.stringify({ id: modalId, element: mediaId })
  )
}

const updatePdpZoomTrigger = (swiperEl, trigger) => {
  if (!trigger) return

  const activeSlide = swiperEl.querySelector('.swiper-slide-active')
  const mediaId = activeSlide?.dataset.mediaId
  const isImage = activeSlide?.dataset.mediaType === 'image'

  if (!isImage || !mediaId) {
    trigger.classList.add('hidden')
    return
  }

  const slides = [...swiperEl.querySelectorAll('.swiper-slide')]
  const index = slides.indexOf(activeSlide) + 1

  trigger.classList.remove('hidden')
  trigger.dataset.mediaId = mediaId
  trigger.setAttribute(
    'aria-label',
    window.accessibilityStrings.openMedia.replace('__INDEX__', index)
  )
}

const getPdpPaginationBullets = (swiperEl) => [
  ...swiperEl.querySelectorAll('.swiper-pagination-bullet'),
]

const getActiveSlideIndex = (swiperEl) =>
  [...swiperEl.querySelectorAll('.swiper-slide')].findIndex((slide) =>
    slide.classList.contains('swiper-slide-active')
  )

// Set only while a play button holds focus that was handed to it from a pagination bullet
// (see focusActiveVideoControl). Tab rerouting applies to that hand-off alone; a play
// button reached by ordinary tabbing must keep native DOM order.
let playButtonFocusedFromPagination = null

const trackPaginationHandoff = (playButton) => {
  playButtonFocusedFromPagination = playButton
  playButton.addEventListener(
    'blur',
    () => {
      if (playButtonFocusedFromPagination === playButton) {
        playButtonFocusedFromPagination = null
      }
    },
    { once: true }
  )
}

const TAB_STOP_SELECTOR = [
  'a[href]',
  'button:not([disabled])',
  'input:not([disabled]):not([type="hidden"])',
  'select:not([disabled])',
  'textarea:not([disabled])',
  'summary',
  'hh-button:not([disabled])',
  '[tabindex]:not([tabindex="-1"])',
].join(', ')

// Only one radio per group is a tab stop: the checked one, or the first enabled one when
// the group has no selection. Groups are scoped by form owner, so the sticky add to
// cart's size radios stay separate from the ones in product info.
const isTabbableRadio = (radio) => {
  if (radio.checked || !radio.name) return true

  const group = [
    ...document.querySelectorAll(
      `input[type="radio"][name="${CSS.escape(radio.name)}"]`
    ),
  ].filter((el) => !el.disabled && el.form === radio.form)

  return group[0] === radio && !group.some((el) => el.checked)
}

const getTabStopAfter = (element) => {
  const tabStops = [...document.querySelectorAll(TAB_STOP_SELECTOR)].filter(
    (el) => {
      if (el.closest('[inert]')) return false
      // offsetParent is null on position: fixed elements such as the sticky add to
      // cart, so measure boxes instead to test whether the element is rendered.
      if (el.getClientRects().length === 0) return false
      return el.matches('input[type="radio"]') ? isTabbableRadio(el) : true
    }
  )

  const index = tabStops.indexOf(element)
  return index < 0 ? null : tabStops[index + 1]
}

const focusTabStop = (element) => {
  // hh-button keeps its real control in shadow DOM, so the host can't take focus.
  const target = element.shadowRoot?.querySelector('button, a[href]') ?? element
  target.focus({ focusVisible: true })
}

// The play button sits before the pagination in the DOM, so after the hand-off Tab would
// restart at Dot 1. Instead continue from where the user was: Tab → next bullet after the
// active slide, Shift+Tab → the active slide's bullet.
const onVideoPlayButtonKeydown = (event) => {
  if (event.key !== 'Tab') return

  const playButton = event.currentTarget
  if (playButtonFocusedFromPagination !== playButton) return

  const swiperEl = playButton.closest('.swiper')
  if (!swiperEl) return

  const bullets = getPdpPaginationBullets(swiperEl)
  const activeIndex = getActiveSlideIndex(swiperEl)
  if (activeIndex < 0 || bullets.length === 0) return

  let target
  if (event.shiftKey) {
    target = bullets[activeIndex]
  } else {
    // On the last slide there is no bullet left, so leave the gallery instead.
    target =
      bullets[activeIndex + 1] ?? getTabStopAfter(bullets[bullets.length - 1])
  }

  if (!target) return

  event.preventDefault()
  focusTabStop(target)
}

// The play/pause button sits before the pagination in the DOM, so a keyboard or screen
// reader user who reaches a video slide by activating a pagination dot can never tab
// back to it. Move focus onto the control instead.
const focusActiveVideoControl = (swiperEl) => {
  // Only take focus if the user is already navigating inside the gallery.
  const previouslyFocused = document.activeElement
  if (!swiperEl.contains(previouslyFocused)) return

  const cameFromBullet =
    previouslyFocused.classList.contains('swiper-pagination-bullet')

  // Runs after setSlidesA11y has made the active slide's controls focusable.
  setTimeout(() => {
    const activeSlide = swiperEl.querySelector('.swiper-slide-active')
    if (activeSlide?.dataset.mediaType !== 'video') return

    const playButton = activeSlide.querySelector('.thumbnail-play')
    if (!playButton) return

    if (cameFromBullet) trackPaginationHandoff(playButton)

    playButton.focus({ focusVisible: true })
  }, 0)
}

const setSlidesA11y = (swiper) => {
  const isPdpMediaGallery =
    swiper.closest('hh-swiper')?.dataset?.pdpMediaGallery === 'true'
  setTimeout(() => {
    swiper.querySelector('.swiper-notification')?.remove()
    swiper.querySelector('.swiper-wrapper').removeAttribute('aria-live')
    // Cart carousels share one set of arrows across tabs, so aria-controls
    // would point at whichever swiper bound them last.
    if (swiper.closest('cart-product-tabs')) {
      document.querySelectorAll('.js-cart-arrow')?.forEach((el) => {
        el.removeAttribute('aria-controls')
      })
    }
    if (swiper.dataset.productCardMedia == 'true') {
      const arrows = swiper.querySelectorAll('.swiper-button')
      arrows.forEach((arrow) => {
        arrow.removeAttribute('aria-controls')
      })
    }
  }, 0)

  setTimeout(() => {
    const visibleSlides = swiper.querySelectorAll('.swiper-slide-visible')
    const hasVisibleSlide = visibleSlides.length > 0
    const hasActiveSlide =
      isPdpMediaGallery &&
      swiper.querySelector('.swiper-slide-active') != null
    // Skip when container is hidden (e.g. in a hidden tab): Swiper won't mark any slide visible,
    // and we must not apply inert to all slides or links become unclickable.
    if ((!hasVisibleSlide && !hasActiveSlide) || !swiper.offsetParent) return

    swiper.querySelectorAll('.swiper-slide').forEach((slide) => {
      const isVisible =
        slide.classList.contains('swiper-slide-visible') ||
        (isPdpMediaGallery && slide.classList.contains('swiper-slide-active'))

      if (isVisible) {
        slide.removeAttribute('inert')
        slide.removeAttribute('aria-hidden')
        const focusable = slide.querySelectorAll('a, hh-button, button')
        focusable.forEach((el) => {
          el.removeAttribute('tabindex')
        })
      } else {
        slide.setAttribute('inert', '')
        slide.setAttribute('aria-hidden', 'true')
        const focusable = slide.querySelectorAll('a, hh-button, button')
        focusable.forEach((el) => {
          el.setAttribute('tabindex', '-1')
        })
      }
    })

    // Keep pagination bullets in the keyboard tab order (Swiper marks them -1),
    // so focus flows slide content -> pagination -> product info in DOM order.
    if (isPdpMediaGallery) {
      swiper.querySelectorAll('.swiper-pagination-bullet').forEach((bullet) => {
        bullet.setAttribute('tabindex', '0')
      })

      // Wire Tab-from-play to continue at the next bullet (not Dot 1).
      swiper
        .querySelectorAll('.swiper-slide[data-media-type="video"] .thumbnail-play')
        .forEach((playButton) => {
          if (playButton.dataset.pdpTabExitBound) return
          playButton.addEventListener('keydown', onVideoPlayButtonKeydown)
          playButton.dataset.pdpTabExitBound = 'true'
        })
    }
  }, 0)

  if (swiper.closest('hh-swiper').dataset?.overrideButtonControls) {
    swiper
      .querySelectorAll('.swiper-button-prev, .swiper-button-next')
      ?.forEach((button) => {
        setTimeout(() => {
          button.removeAttribute('aria-controls')
          if (button.classList.contains('swiper-button-next')) {
            button.setAttribute('aria-label', 'Next set of slides')
          } else if (button.classList.contains('swiper-button-prev')) {
            button.setAttribute('aria-label', 'Previous set of slides')
          }
        }, 0)
      })
  }
}

class HHSwiper extends HTMLElement {
  static observer = new IntersectionObserver(
    (entries, observer) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          entry.target.initSwiper()
          observer.unobserve(entry.target)
        }
      })
    },
    { rootMargin: '200px' }
  )

  connectedCallback() {
    HHSwiper.observer.observe(this)
  }

  initSwiper() {
    if (this.swiper) return

    this.swiper = new Swiper(
      this.querySelector(`.swiper--${this.dataset.id}`),
      {
        direction: this.dataset.verticalScroll ? 'vertical' : 'horizontal',
        loop: !Boolean(this.dataset.cancelLoop),
        effect: this.dataset.effect ? this.dataset.effect : 'slide',
        fadeEffect: {
          crossFade: true,
        },
        watchSlidesProgress: true,
        mousewheel: Boolean(this.dataset.mousewheel),
        navigation: {
          nextEl: this.dataset.customNext
            ? document.querySelector(this.dataset.customNext)
            : this.querySelector(`.swiper-button-next--${this.dataset.id}`),
          prevEl: this.dataset.customPrevious
            ? document.querySelector(this.dataset.customPrevious)
            : this.querySelector(`.swiper-button-prev--${this.dataset.id}`),
          enabled: Boolean(this.dataset.mobileNav),
        },
        slidesPerView: this.dataset.slidesPerView
          ? parseFloat(this.dataset.slidesPerView.split(',')[0])
          : 1,
        slidesPerGroup: this.dataset.slidesPerGroup
          ? parseFloat(this.dataset.slidesPerGroup.split(',')[0])
          : 1,
        scrollbar: {
          el: `.swiper-scrollbar--${this.dataset.id}`,
          enabled: true,
        },
        spaceBetween: this.dataset.spaceBetween
          ? parseFloat(this.dataset.spaceBetween.split(',')[0])
          : 0,
        pagination: {
          el: `.swiper-pagination--${this.dataset.id}`,
          ...(this.dataset.accessiblePagination
            ? {
                clickable: true,
                renderBullet: function (index, className) {
                  return (
                    '<button type="button" class="' +
                    className +
                    '" aria-label="' +
                    window.accessibilityStrings.goToSlide.replace(
                      '{{ index }}',
                      index + 1
                    ) +
                    '"></button>'
                  )
                },
              }
            : {}),
        },
        breakpoints: {
          750: {
            scrollbar: {
              enabled: false,
            },
            navigation: {
              enabled: true,
            },
            slidesPerView: this.dataset.slidesPerView
              ? parseFloat(this.dataset.slidesPerView.split(',')[1])
              : 1,
            slidesPerGroup: this.dataset.slidesPerGroup
              ? parseFloat(this.dataset.slidesPerGroup.split(',')[1])
              : 1,
            spaceBetween: this.dataset.spaceBetween
              ? parseFloat(this.dataset.spaceBetween.split(',')[1])
              : 0,
          },
          990: {
            scrollbar: {
              enabled: false,
            },
            navigation: {
              enabled: true,
            },
            slidesPerView: this.dataset.slidesPerView
              ? parseFloat(this.dataset.slidesPerView.split(',')[2])
              : 1,
            slidesPerGroup: this.dataset.slidesPerGroup
              ? parseFloat(this.dataset.slidesPerGroup.split(',')[2])
              : 1,
            spaceBetween: this.dataset.spaceBetween
              ? parseFloat(this.dataset.spaceBetween.split(',')[0])
              : 0,
          },
        },
        autoplay: false,
        nested: Boolean(this.dataset.nested),
        ...(this.dataset.slideLabelMessage
          ? {
              a11y: {
                slideLabelMessage: this.dataset.slideLabelMessage,
              },
            }
          : {}),
        on: {
          init() {
            setSlidesA11y(this.el)
            if (this.el.closest('hh-swiper')?.dataset?.pdpMediaGallery) {
              updatePdpZoomTrigger(
                this.el,
                this.el.querySelector('.js-pdp-zoom-trigger')
              )
            }
          },
          slideChange() {
            if (this.el.closest('hh-swiper')?.dataset?.pdpMediaGallery) {
              updatePdpZoomTrigger(
                this.el,
                this.el.querySelector('.js-pdp-zoom-trigger')
              )
            }
          },
          slideChangeTransitionEnd() {
            setSlidesA11y(this.el)
            if (this.el.closest('hh-swiper')?.dataset?.pdpMediaGallery) {
              updatePdpZoomTrigger(
                this.el,
                this.el.querySelector('.js-pdp-zoom-trigger')
              )
              focusActiveVideoControl(this.el)
            }
          },
        },
      }
    )

    if (this.dataset.pdpInfoOnLast) {
      this.swiper.on('sliderMove', function (pointermove) {
        const activeIndex = this.activeIndex
        const slidesLength = this.slides.length - 1
        const userSwipingNext =
          pointermove.swipeDirection == 'next' ? true : false

        if (activeIndex == slidesLength && userSwipingNext) {
          document.body.classList.remove('not-swiped')
          document.body.classList.add('swiped-up')
        }
      })
    }

    this.dispatchEvent(
      new CustomEvent('swiper:init', {
        bubbles: true,
        detail: { swiper: this.swiper, id: this.dataset.id },
      })
    )

    if (this.dataset.pdpMediaGallery) {
      const zoomTrigger = this.querySelector('.js-pdp-zoom-trigger')
      const modalId = this.dataset.modalId

      const swiperEl = this.querySelector(`.swiper--${this.dataset.id}`)

      zoomTrigger?.addEventListener('click', () => {
        const activeSlide = swiperEl?.querySelector('.swiper-slide-active')
        const mediaId = activeSlide?.dataset.mediaId
        const isImage = activeSlide?.dataset.mediaType === 'image'

        if (isImage && mediaId && modalId) {
          openPdpMediaModal(modalId, mediaId)
        }
      })
    }

    if (this.dataset.mobileOnly) {
      const swiperResponsiveToggle = () => {
        const isDesktop = window.innerWidth > 800

        if (isDesktop && this.swiper.initialized) {
          this.swiper.destroy(false, true)
        } else if (!isDesktop && this.swiper.destroyed) {
          this.swiper.init()
        }
      }

      swiperResponsiveToggle()
      const throttledResize = utils.throttle(swiperResponsiveToggle, 100)
      window.addEventListener('resize', throttledResize)
    }
  }

  disconnectedCallback() {
    HHSwiper.observer.unobserve(this)
    this.swiper?.destroy()
    this.swiper = null
  }
}

customElements.define('hh-swiper', HHSwiper)
